import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import { UserAuth } from '../context/AuthContext'
import { LibroDiario } from '../models/LibroContable'
import { OrganizationAPI } from '../api/organization.api'

interface ComprobantesFilters {
  status?: string,
  type: 'todos' | 'aprobados' | 'borradores',
  accountId: number,
  onlyCreated?: boolean
}

interface UseInfiniteComprobantesParams {
  businessId: number
  idCuenta: number
  dateFrom: string
  dateTo: string
  limit: number
  filters: ComprobantesFilters
}

export const useInfiniteComprobantes = ({
  businessId,
  dateFrom,
  dateTo,
  limit = 30,
  filters
}: UseInfiniteComprobantesParams) => {
  const { isAuthenticated } = UserAuth()
  const organizationAPI = new OrganizationAPI()

  return useInfiniteQuery({
    enabled: isAuthenticated && !!businessId,
    queryKey: [
      QueryKeys.LIBRO_MAYOR,
      businessId,
      dateFrom,
      dateTo,
      limit,
      filters
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await organizationAPI.getLibroComprobantes(
        businessId,
        dateFrom,
        dateTo,
        filters.type,
        limit,
        pageParam,
        filters.accountId,
        filters.status,
        filters.onlyCreated
      )

      const comprobantesData = response.data

      const isPaginatedResponse = 'count' in comprobantesData && 'libros' in comprobantesData
      
      const libros = isPaginatedResponse
        ? (comprobantesData as { libros: LibroDiario[], count: number }).libros
        : comprobantesData as LibroDiario[]
      
      const count = isPaginatedResponse
        ? (comprobantesData as { count: number }).count
        : libros.length

      if (!libros || libros.length === 0) {
        return {
          data: [],
          totalPages: 0,
          currentPage: pageParam,
          hasNextPage: false,
          count: 0
        }
      }

      const totalPages = Math.ceil(count / limit)
      
      return {
        data: libros,
        totalPages,
        currentPage: pageParam,
        hasNextPage: pageParam + 1 < totalPages,
        count
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined
    },
    initialPageParam: 0
  })
}